<template>
    <form
        :class="{ 'was-validated': wasValidated }"
        v-on:submit.prevent
        v-if="item"
        :id="id"
        novalidate
        autocomplete="off"
    >
        <slot :item="item" name="header"></slot>
        <!-- Loop through configured fields -->
        <div v-for="field in fields" :key="field.key">
            <div v-if="field.type == 'divider'" v-show="!checkHidden(field)" :class="{'pt-4': field.name, 'py-1': !field.name}">
                <p class="mb-0 small font-weight-bold form-divider-label text-secondary">{{ field.name }}</p>
                <hr class="mt-1 pt-1" :class="{'mb-3': !field.subtext, 'mb-2': field.subtext}">
                <p v-if="field.subtext" class="mb-2 small text-secondary">{{ field.subtext }}</p>
            </div>
            <div v-else-if="field.type == 'spacer'" class="py-3" v-show="!checkHidden(field)"></div>
            <div v-else-if="!field.formExclude" v-show="!checkHidden(field)" class="form-group row" :class="{'mb-dense': condensedStyle || field.condensedStyle, 'ml-3': field.isSubOption}">
                <slot :name="field.key + '-header'"></slot>
                <!-- Search -->
                <template v-if="field.type === 'search' && !checkHidden(field)">
                        <label :for="id + field.key" class="col-form-label" :class="[labelClass(field)]" v-if="field.name">
                            <span>{{ field.name }}</span>
                            <div v-show="field.loading" class="spinner-border loading-spinner-sm text-primary ml-2" role="status">
                                <span class="sr-only">Loading...</span>
                            </div>
                            <help-text v-if="field.description" :content="field.description" class="ml-1"></help-text>
                        </label>
                        <div :class="[inputClass(field)]">
                            <base-search
                                :disabled="checkDisabled(field)"
                                :placeholder="field.placeholder"
                                v-model="item[field.key]"
                                :options="field.options"
                                :value-field="field.valueField"
                                :label-field="field.labelField"
                                :search-fields="field.searchFields"
                                :sort-field="field.sortField"
                                :required="checkRequired(field)"
                                :option-render="field.optionRender"
                                :item-render="field.optionRender"
                                :max-items="field.maxItems"
                            >
                            </base-search>
                            <div v-if="field.subtext" class="small text-secondary mt-1" style="margin-left: 2px;">{{ field.subtext }}</div>
                            <div v-if="field.required" class="invalid-feedback">{{ field.name || "This field" }} is required.</div>
                        </div>
                </template>
                <!-- Select -->
                <template v-else-if="field.type === 'select'">
                    <label :for="id + field.key" class="col-form-label" :class="[labelClass(field)]" v-if="field.name">
                        {{ field.name }}
                        <help-text v-if="field.description" :content="field.description" class="ml-1"></help-text>
                    </label>
                    <div :class="[inputClass(field)]" class="pb-1">
                        <select
                            v-model="item[field.key]"
                            :id="id + field.key"
                            :required="checkRequired(field)"
                            :disabled="checkDisabled(field)"
                            class="form-control"
                            :class="{'w-auto': field.autoWidth}"
                        >
                            <option
                                v-for="option in field.options"
                                :key="option.value"
                                :value="option.value"
                                :disabled="checkDisabled(option)"
                            >{{ option.label }}
                            </option>
                        </select>
                        <div v-if="field.subtext" class="small text-secondary mt-1">{{ field.subtext }}</div>
                        <div v-if="field.required" class="invalid-feedback">{{ field.name || "This field" }} is required.</div>
                    </div>
                </template>
                <!-- Date -->
                <template v-else-if="field.type === 'date'">
                    <label :for="id + field.key" class="col-form-label" :class="[labelClass(field)]" v-if="field.name">
                        {{ field.name }}
                        <help-text v-if="field.description" :content="field.description" class="ml-1"></help-text>
                    </label>
                    <div :class="[inputClass(field)]">
                        <input
                            v-model="item[field.key]"
                            :id="id + field.key"
                            :required="checkRequired(field)"
                            :disabled="checkDisabled(field)"
                            type="date"
                            class="form-control"
                        />
                        <div v-if="field.subtext" class="small text-secondary mt-1">{{ field.subtext }}</div>
                        <div v-if="field.required" class="invalid-feedback">{{ field.name || "This field" }} is required.</div>
                    </div>
                </template>
                <!-- Time -->
                <template v-else-if="field.type === 'time'">
                    <label :for="id + field.key" class="col-form-label" :class="[labelClass(field)]" v-if="field.name">
                        {{ field.name }}
                        <help-text v-if="field.description" :content="field.description" class="ml-1"></help-text>
                    </label>
                    <div :class="[inputClass(field)]">
                        <input
                            v-model="item[field.key]"
                            :id="id + field.key"
                            :required="checkRequired(field)"
                            :disabled="checkDisabled(field)"
                            type="time"
                            class="form-control"
                        />
                        <div v-if="field.subtext" class="small text-secondary mt-1">{{ field.subtext }}</div>
                        <div v-if="field.required" class="invalid-feedback">{{ field.name || "This field" }} is required.</div>
                    </div>
                </template>
                <!-- Checkbox -->
                <template v-else-if="field.type === 'checkbox'">
                    <div 
                        class="col-12"
                        :class="[field.positionIndent ? indentClass : '']"
                    >
                        <div class="custom-control custom-checkbox pb-1">
                            <input
                                v-model="item[field.key]"
                                :id="id + field.key"
                                :required="checkRequired(field)"
                                :disabled="checkDisabled(field)"
                                type="checkbox"
                                class="custom-control-input"
                            />
                            <label class="custom-control-label" :for="id + field.key">
                                <span>{{ field.name }}</span>
                                <span
                                    v-if="field.badge"
                                    :class="[field.badgeClass || 'badge-gray']"
                                    class="badge badge-tiny ml-2 align-middle"
                                >
                                    <span v-if="field.badgeIconClass" :class="[field.badgeIconClass]" class="icon icon-100 mr-1"></span>
                                    <span>{{ field.badge }}</span>
                                </span>
                            </label>
                            <a v-if="field.editBtnText" @click="$emit('edit', field.key)" class="align-middle badge badge-gray badge-tiny ml-2">
                                <span class="icon icon-100 icon-glyphicons-basic-pencil"></span>
                                {{ field.editBtnText }}
                            </a>
                            <help-text v-if="field.description" :content="field.description" class="ml-2"></help-text>
                            <div v-if="field.subtext" class="small text-secondary mt-1">{{ field.subtext }}</div>
                        </div>
                    </div>
                </template>
                <!-- Switch -->
                <template v-else-if="field.type === 'switch'">
                    <div 
                        class="col-12"
                        :class="[field.positionIndent ? indentClass : '']"
                    >
                        <div class="custom-control custom-switch pb-1">
                            <input
                                v-model="item[field.key]"
                                :disabled="checkDisabled(field)"
                                type="checkbox"
                                class="custom-control-input"
                                :id="id + field.key"
                            >
                            <label class="custom-control-label" :for="id + field.key">
                                <span>{{ field.name }}</span>
                                <span
                                    v-if="field.badge"
                                    :class="[field.badgeClass || 'badge-gray']"
                                    class="badge badge-tiny ml-2 align-middle"
                                >
                                    <span v-if="field.badgeIconClass" :class="[field.badgeIconClass]" class="icon icon-100 mr-1"></span>
                                    <span>{{ field.badge }}</span>
                                </span>
                            </label>
                            <help-text v-if="field.description" :content="field.description" class="ml-2"></help-text>
                            <div v-if="field.subtext" class="small text-secondary mt-1 mb-1">{{ field.subtext }}</div>
                        </div>
                    </div>
                </template>
                <!-- Radio -->
                <template v-else-if="field.type === 'radio'">
                    <label :for="id + field.key" :class="[labelClass(field)]" v-if="field.name">
                        {{ field.name }}
                        <help-text v-if="field.description" :content="field.description" class="ml-1"></help-text>
                        <div v-if="field.subtext" class="small text-secondary mt-1 mb-1">{{ field.subtext }}</div>
                    </label>
                    <div class="col-12" :class="[inputClass(field)]">
                        <div
                            v-for="option in field.options" :key="option.key"
                            class="custom-control custom-radio mb-2"
                            :class="{'custom-control-inline': field.inlineStyle}"
                        >
                            <input
                                v-model="item[field.key]"
                                :value="option.value"
                                class="custom-control-input"
                                type="radio"
                                :id="id + field.key + option.value"
                                :disabled="checkDisabled(option)"
                            />
                            <label class="custom-control-label" :for="id + field.key + option.value">
                                <span>{{ option.label }}</span>
                                <span
                                    v-if="option.badge"
                                    :class="[option.badgeClass || 'badge-gray']"
                                    class="badge badge-small ml-2 align-text-bottom"
                                >{{ option.badge }}</span>
                                <p class="text-secondary small mb-1" v-if="option.subtext">{{ option.subtext }}</p>
                            </label>
                            <help-text v-if="option.description" :content="option.description" class="ml-2"></help-text>
                        </div>
                    </div>
                </template>
                <!-- Textarea -->
                <template v-else-if="field.type === 'textarea'">
                    <label :for="id + field.key" class="col-form-label" :class="[labelClass(field)]" v-if="field.name">
                        {{ field.name }}
                        <help-text v-if="field.description" :content="field.description" class="ml-1"></help-text>
                    </label>
                    <div :class="[inputClass(field)]" class="pb-1">
                        <textarea
                            v-model="item[field.key]"
                            :maxlength="field.maxlength ? field.maxlength : ''"
                            :id="id + field.key"
                            :placeholder="field.placeholder ? field.placeholder : field.name"
                            :required="checkRequired(field)"
                            :disabled="checkDisabled(field)"
                            class="form-control"
                            :rows="field.rows ? field.rows : 15"
                            dir="auto"
                        ></textarea>
                        <div v-if="field.subtext || field.showLimit" class="small text-secondary mt-1">
                            <span v-if="field.subtext" class="pr-1">{{ field.subtext }}</span>
                            <span v-if="field.showLimit">({{ (item[field.key] || "").length }}/{{ field.maxlength }})</span>
                        </div>
                        <div v-if="field.required" class="invalid-feedback">{{ field.name || "This field" }} is required.</div>
                    </div>
                </template>
                <!-- Number -->
                <template v-else-if="field.type === 'number'">
                    <label
                        :for="id + field.key"
                        class="col-form-label"
                        :class="[labelClassNumberField(field)]"
                        v-if="field.name"
                    >
                        {{ field.name }}
                        <help-text v-if="field.description" :content="field.description" class="ml-1"></help-text>
                    </label>
                    <div :class="[inputClassNumberField(field)]">
                        <div class="input-group">
                            <input
                                v-model.number="item[field.key]"
                                :id="id + field.key"
                                :placeholder="field.placeholder ? field.placeholder : field.name"
                                :required="checkRequired(field)"
                                :disabled="checkDisabled(field)"
                                type="number"
                                class="form-control"
                                min="0"
                                :step="field.step ? field.step : 'any'"
                            />
                            <div v-if="field.unit" class="input-group-append">
                                <div class="input-group-text">{{ field.unit }}</div>
                            </div>
                        </div>
                        <div v-if="field.subtext" class="small text-secondary mt-1">{{ field.subtext }}</div>
                        <div v-if="field.required" class="invalid-feedback">{{ field.name || "This field" }} is required.</div>
                    </div>
                </template>
                <!-- Currency -->
                <template v-else-if="field.type === 'currency'">
                    <label :for="id + field.key" class="col-form-label" :class="[labelClass(field)]" v-if="field.name">
                        {{ field.name }}
                        <help-text v-if="field.description" :content="field.description" class="ml-1"></help-text>
                    </label>
                    <div :class="[inputClass(field)]">
                        <div class="input-group">
                            <div class="input-group-prepend">
                                <div class="input-group-text">$</div>
                            </div>
                            <input
                                v-model.number="item[field.key]"
                                :id="id + field.key"
                                :placeholder="field.placeholder ? field.placeholder : field.name"
                                :required="checkRequired(field)"
                                :disabled="checkDisabled(field)"
                                type="number"
                                class="form-control"
                                min="0"
                                step="0.01"
                            />
                        </div>
                        <div class="small text-secondary mt-1">{{ field.subtext }}</div>
                        <div v-if="field.required" class="invalid-feedback">{{ field.name || "This field" }} is required.</div>
                    </div>
                </template>
                <!-- Rich Text -->
                <template v-else-if="field.type === 'richtext'">
                    <label :for="id + field.key" class="col-form-label" :class="[labelClass(field)]" v-if="field.name">
                        {{ field.name }}
                        <help-text v-if="field.description" :content="field.description" class="ml-1"></help-text>
                    </label>
                    <div :class="[inputClass(field)]">
                        <rich-text
                            v-model="item[field.key]"
                            :placeholder="field.placeholder ? field.placeholder : field.name"
                            :id="id + field.key"
                            :default-content="field.defaultContent"
                        ></rich-text>
                        <div v-if="field.subtext" class="small text-secondary mt-1">{{ field.subtext }}</div>
                        <div v-if="field.required" class="invalid-feedback">{{ field.name || "This field" }} is required.</div>
                    </div>
                </template>
                <!-- Color -->
                <template v-else-if="field.type === 'color'">
                    <label :for="id + field.key" class="col-form-label" :class="[labelClass(field)]" v-if="field.name">
                        {{ field.name }}
                        <help-text v-if="field.description" :content="field.description" class="ml-1"></help-text>
                    </label>
                    <div :class="[inputClass(field)]">
                        <div class="d-flex">
                            <div class="flex-grow-0">
                                <input
                                    type="color"
                                    v-model="item[field.key]"
                                    class="form-control btn color-input bg-light border-0 rounded h-100 mr-2"
                                />
                            </div>
                            <div class="flex-grow-0">
                                <input
                                    v-model="item[field.key]"
                                    :maxlength="field.maxlength ? field.maxlength : ''"
                                    :id="id + field.key"
                                    :placeholder="field.placeholder ? field.placeholder : field.name"
                                    :required="checkRequired(field)"
                                    :disabled="checkDisabled(field)"
                                    type="text"
                                    class="form-control color-hex-input"
                                />
                            </div>
                        </div>
                        <div v-if="field.subtext" class="small text-secondary mt-1">{{ field.subtext }}</div>
                        <div v-if="field.required" class="invalid-feedback">{{ field.name || "This field" }} is required.</div>
                    </div>
                </template>
                <!-- Text -->
                <template v-else>
                    <label :for="id + field.key" class="col-form-label" :class="[labelClass(field)]" v-if="field.name">
                        <span v-if="!field.hideName">{{ field.name }}</span>
                        <help-text v-if="field.description" :content="field.description" class="ml-1"></help-text>
                    </label>
                    <div :class="[inputClass(field)]">
                        <input
                            v-model="item[field.key]"
                            :maxlength="field.maxlength ? field.maxlength : ''"
                            :id="id + field.key"
                            :placeholder="field.placeholder ? field.placeholder : field.name"
                            :required="checkRequired(field)"
                            :disabled="checkDisabled(field)"
                            type="text"
                            class="form-control"
                            dir="auto"
                        />
                        <div v-if="field.subtext || field.showLimit" class="small text-secondary mt-1">
                            <span v-if="field.subtext" class="pr-1">{{ field.subtext }}</span>
                            <span v-if="field.showLimit">({{ (item[field.key] || "").length }}/{{ field.maxlength }})</span>
                        </div>
                        <div v-if="field.required" class="invalid-feedback">{{ field.name || "This field" }} is required.</div>
                    </div>
                </template>
                <slot :name="field.key + '-footer'"></slot>
            </div>
        </div>
        <slot :item="item" name="footer"></slot>
    </form>
</template>

<script>
import BaseSearch from "@/components/BaseSearch"
import RichText from "@/components/RichText"
import HelpText from "@/components/HelpText"

export default {
    name: "BaseForm",
    components: {
        BaseSearch,
        RichText,
        HelpText
    },
    props: {
        // an object representing the state of the form
        value: {
            type: Object,
            required: false
        },
        // configuration for the fields in the form
        // name: required, label to be used for the form field
        // description: optional, description to be displayed as help text
        // subtext: optional, small text to be displayed below label
        // key: required, property to bind to the form field
        // required: required, controls whether the field is required
        // maxlength: optional, max length for text fields
        // disabled: optional, function returning a boolean that disables the field if true
        // hidden: optional, function returning a boolean that hides the field if true
        // rows: optional, the number of rows to display if type is textarea
        // placeholder: optional, a placeholder to display
        // type: required, the type of form field (see above template for options)
        // type 'search' - uses BaseSearch component, options will be passed on
        fields: {
            type: Array,
            required: true
        },
        inlineStyle: {
            type: Boolean,
            required: false,
            default: false
        },
        condensedStyle: {
            type: Boolean,
            required: false,
            default: false
        },
        indentColumns: {
            type: Number,
            required: false,
            default() {
                return 3
            }
        },
        disabled: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    data() {
        return {
            wasValidated: false,
            item: null
        }
    },
    computed: {
        inlineLabelClass() {
            return `col-sm-${this.indentColumns}`
        },
        inlineInputClass() {
            return `col-sm-${12 - this.indentColumns}`
        },
        indentClass() {
            return `${this.inlineInputClass} offset-sm-${this.indentColumns}`
        },
        defaultLabelClass() {
            return this.inlineStyle ? this.inlineLabelClass : 'col-12'
        },
        defaultInputClass() {
            return this.inlineStyle ? this.inlineInputClass : 'col-12'
        },
    },
    watch: {
        item() {
            this.$emit("input", this.item)
        },
        value() {
            this.item = this.value
        }
    },
    methods: {
        // Validate the form.
        validate() {
            var form = document.getElementById(this.id)
            this.wasValidated = true
            return form.checkValidity()
        },
        // Reset the validation state on the form.
        resetValidate() {
            this.wasValidated = false
        },
        // Check if the field should be disabled.
        checkDisabled(field) {
            if (this.disabled) {
                return true
            }
            if (field.disabled && typeof field.disabled === "boolean") {
                return field.disabled
            } else if (field.disabled && typeof field.disabled === "function") {
                return field.disabled(this.value)
            } else {
                return false
            }
        },
        // Check if the field should be required.
        checkRequired(field) {
            if (field.required && typeof field.required === "boolean") {
                return field.required
            } else if (field && typeof field.required === "function") {
                return field.required(this.value)
            } else {
                return false
            }
        },
        checkHidden(field) {
            if (field.hidden) {
                return field.hidden(this.value)
            }
            return false
        },
        // Default to the form settings for inline display, with the ability to override
        // at the field level.
        labelClass(field) {
            return field.displayInline ? this.inlineLabelClass : this.defaultLabelClass
        },
        inputClass(field) {
            return field.displayInline ? this.inlineInputClass : this.defaultInputClass
        },
        labelClassNumberField(field) {
            if (this.inlineStyle) {
                return this.labelClass(field)
            } else if (field.displayInline) {
                return 'col-sm-6'
            } else {
                return 'col-12'
            }
        },
        inputClassNumberField(field) {
            if (this.inlineStyle) {
                return this.inputClass(field)
            } else if (field.displayInline) {
                return 'col-sm-6'
            } else {
                return 'col-12'
            }
        }
    },
    created() {
        // The unique identifier is used to link labels to elements while being able to handle
        // multiple instances of this component on the same page.
        this.id = this._uid
        this.item = this.value
    }
}
</script>

<style lang="scss" scoped>
.form-group {
    margin-bottom: 1.02rem;
}

.loading-spinner-sm {
    width: 0.8rem;
    height: 0.8rem;
    border-width: 0.15em;
}

.mb-dense {
    margin-bottom: 0.4rem;
}
</style>